import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import ArrowLeftIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowLeft';
import yupResolver from '@wix/da-shared-react/pkg/utils/yupResolver';
import { saveShippingProfile } from '../../redux/actions/shippingProfiles';
import {
  getShippingDestinations,
  getShippingProfilesSavingStatus,
} from '../../redux/selectors/shippingProfiles';
import { ShippingProfile, ShippingDestination, ShippingRate } from '../../redux/types';
import ProfileForm, { ProfileFormValues } from './_partials/ProfileForm';
import { useProfileFormValidation } from './validation';

import s from './ShippingProfileEditModal.scss';

interface Props {
  profile?: ShippingProfile;
  onRequestClose: () => void;
}

function destinationKey(dest: ShippingDestination): string {
  return `${dest.destinationType ?? ''}:${dest.destinationId ?? ''}`;
}

function keyToRate(key: string, destinations: ShippingDestination[]): Partial<ShippingRate> {
  const dest = destinations.find(d => destinationKey(d) === key);
  if (!dest) {
    return {};
  }
  return {
    destinationType: dest.destinationType,
    destinationId: dest.destinationId,
  };
}

const ShippingProfileEditModal: React.FC<Props> = ({ profile, onRequestClose }) => {
  const { t } = useTranslation();

  const destinations = useSelector(getShippingDestinations);
  const savingStatus = useSelector(getShippingProfilesSavingStatus);
  const dispatchSaveProfile = useAction(saveShippingProfile);
  const validationSchema = useProfileFormValidation();

  const isSaving = savingStatus === 'saving';

  const form = useForm<ProfileFormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema, { abortEarly: false }),
    defaultValues: {
      title: profile?.title ?? '',
      rates:
        profile && profile.rates.length > 0
          ? profile.rates.map(r => ({
              destinationKey: `${r.destinationType ?? ''}:${r.destinationId ?? ''}`,
              priceUsd: r.priceUsd ?? '0',
            }))
          : [{ destinationKey: '', priceUsd: '' }],
    },
  });

  const onSubmit = useCallback(
    (values: ProfileFormValues) => {
      const validRates = values.rates
        .filter(r => r.destinationKey && r.priceUsd)
        .map(
          r =>
            ({
              ...keyToRate(r.destinationKey, destinations),
              priceUsd: r.priceUsd,
            }) as ShippingRate
        );

      if (validRates.length === 0) {
        return;
      }

      dispatchSaveProfile({
        title: values.title.trim(),
        rates: validRates,
        profileId: profile?.shippingProfileid,
        onSuccess: onRequestClose,
      });
    },
    [destinations, profile, dispatchSaveProfile, onRequestClose]
  );

  const heading = t(`manageShippingProfiles.editProfile.title${profile ? '' : '.new'}`);

  return (
    <FormProvider {...form}>
      <Modal
        isOpen
        onRequestClose={onRequestClose}
        className={s['modal']}
        heading={heading}
        showHeaderBorder
        showFooterBorder
        footerLeft={[
          <TextButton
            key="back"
            variant="main"
            size="medium"
            icon={ArrowLeftIcon}
            onClick={onRequestClose}
          >
            {t('common.back')}
          </TextButton>,
        ]}
        footerRight={[
          <BrandedButton
            key="save"
            variant="brand-green"
            size="large"
            disabled={isSaving}
            onClick={() => void form.handleSubmit(onSubmit)()}
          >
            {t('manageShippingProfiles.editor.saveProfile')}
          </BrandedButton>,
        ]}
      >
        <div className={s['content']}>
          <ProfileForm destinations={destinations} />
        </div>
      </Modal>
    </FormProvider>
  );
};
ShippingProfileEditModal.displayName = 'ShippingProfileEditModal';

export default ShippingProfileEditModal;
